import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LiquidLoader } from "../components/LiquidLoader";
import { PayerPhoneField } from "../components/PayerPhoneField";
import { requestCredentialEdit } from "../api/credentials";
import { Toast } from "../components/Toast";
import {
  createPaymentOrder,
  verifyPayment,
  loadCashfreeScript,
} from "../api/payments";
import { validateCouponApi } from "../api/verification";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePayerPhone } from "../hooks/usePayerPhone";
import { usePrice } from "../hooks/usePrice";
import { formatRupees } from "../utils/money";
import { C, font } from "../styles/tokens";

const PAYMENT_INFO = {
  PROFESSIONAL_MEMBERSHIP: {
    label: "Professional Membership",
    description:
      "Annual membership fee to build a verified profile, request credentials verification, and access platform features.",
  },
  INSTITUTION_SUBSCRIPTION: {
    label: "Academic Institution Subscription",
    description:
      "Annual subscription for academic institutions to process and verify credential requests.",
  },
  ORGANISATION_SUBSCRIPTION: {
    label: "Organisation Subscription",
    description:
      "Annual subscription for organisations to process verifications and request candidate profiles.",
  },
  VERIFICATION_FEE: {
    label: "Credential Verification Request",
    description:
      "Standard fee to initiate verification of a single credential by the verifying authority.",
  },
  ADDITIONAL_OFFICER_FEE: {
    label: "Additional Hiring Officer Fee",
    description:
      "Fee to add an additional hiring / recruitment officer beyond your institution's default quota.",
  },
};

export function CheckoutPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Parse state or search query fallback
  const type = location.state?.type || searchParams.get("type");
  const credentialId =
    location.state?.credentialId || searchParams.get("credentialId");
  const verifierType =
    location.state?.verifierType || searchParams.get("verifierType");
  const verifierId =
    location.state?.verifierId || searchParams.get("verifierId");
  const requestNote =
    location.state?.requestNote || searchParams.get("requestNote");
  const proposedChanges = location.state?.proposedChanges;
  // A change of a verified credential (docs/EDIT_POLICY.md): the server
  // sends it to the verifier who verified it.
  const editChanges = location.state?.editChanges;

  // Payment states: IDLE, CREATING_ORDER, CHECKOUT_OPEN, PROCESSING, VERIFYING, SUCCESS, FAILED, CANCELLED, PENDING
  const [status, setStatus] = useState("IDLE");
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(
    () => `idemp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const payer = usePayerPhone();
  // The amount the server charges (the server prices every order itself).
  const price = usePrice(type);

  // Coupon discount states
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccessMsg, setCouponSuccessMsg] = useState(null);

  const originalAmountPaise = price.pricePaise ?? 50_000;
  const effectiveAmountPaise = appliedCoupon?.valid
    ? appliedCoupon.finalAmountPaise
    : originalAmountPaise;

  const handleApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    if (type !== "VERIFICATION_FEE") {
      setCouponError(
        "Coupons can only be applied to credential verification fees.",
      );
      return;
    }
    const cleanCode = (couponCodeInput || "").trim().toUpperCase();
    if (!cleanCode) {
      setCouponError("Please enter a valid coupon code.");
      return;
    }
    setCouponError(null);
    setCouponSuccessMsg(null);
    setCouponLoading(true);
    try {
      const res = await validateCouponApi({
        code: cleanCode,
        accountType: "PROFESSIONAL",
        verifierType: verifierType || undefined,
        verifierId: verifierId || undefined,
      });
      const data = res?.data || res;
      if (!data?.valid) {
        throw new Error(data?.message || "Invalid coupon code.");
      }
      setAppliedCoupon(data);
      setCouponSuccessMsg(
        data.message || `${data.discountPercent}% discount applied!`,
      );
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to validate coupon code.",
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponError(null);
    setCouponSuccessMsg(null);
  };

  const info = PAYMENT_INFO[type];

  // If type is not recognized, render an error screen
  if (!type || !info) {
    return (
      <AppShell maxWidth={600}>
        <div style={{ ...errorContainerStyle, padding: isMobile ? 20 : 32 }}>
          <h2 style={{ color: C.red, margin: "0 0 10px" }}>
            Invalid Checkout Request
          </h2>
          <p style={{ color: C.textMed, fontSize: 14 }}>
            We could not resolve the payment details. Please return to the
            previous page and try initiating the checkout again.
          </p>
          <button onClick={() => navigate(-1)} style={secondaryButtonStyle}>
            Go Back
          </button>
        </div>
      </AppShell>
    );
  }

  async function handlePay() {
    if (!termsAccepted) {
      setError(
        "Please accept the Terms & Conditions and Privacy Policy before continuing with payment.",
      );
      setStatus("FAILED");
      return;
    }

    const payerPhone = payer.checked();
    if (payerPhone === null) return;

    setError(null);
    setStatus("CREATING_ORDER");

    try {
      // Step 1: Create Order on backend with Terms Acceptance
      const order = editChanges
        ? await requestCredentialEdit(credentialId, {
            changes: editChanges,
            idempotencyKey,
            termsAccepted: true,
            termsVersion: "1.0",
            payerPhone,
            couponCode: appliedCoupon?.valid ? appliedCoupon.code : undefined,
          })
        : await createPaymentOrder(type, {
            payerPhone,
            provider: "CASHFREE",
            credentialId: credentialId || undefined,
            verifierType: verifierType || undefined,
            verifierId: verifierId || undefined,
            requestNote: requestNote?.trim() || undefined,
            proposedChanges: proposedChanges || undefined,
            couponCode: appliedCoupon?.valid ? appliedCoupon.code : undefined,
            idempotencyKey,
            termsAccepted: true,
            termsVersion: "1.0",
          });

      // Bypass handling (if developer mode disables payments or 100% coupon discount)
      if (order?.bypass) {
        setStatus("SUCCESS");
        setPaymentDetails({
          orderId: order.orderId,
          paymentId:
            order.paymentId ||
            (appliedCoupon ? `COUPON_${appliedCoupon.code}` : "Bypass Mode"),
          amount: order.amountPaise ?? 0,
        });
        return;
      }

      // Step 2: Open official Cashfree checkout
      setStatus("CHECKOUT_OPEN");
      await loadCashfreeScript();

      const isProduction =
        (
          import.meta.env.VITE_CASHFREE_ENVIRONMENT ||
          import.meta.env.VITE_CASHFREE_MODE ||
          ""
        ).toLowerCase() === "production" ||
        (typeof window !== "undefined" &&
          (window.location.hostname === "tickandpass.com" ||
            window.location.hostname === "www.tickandpass.com"));

      const cashfree = window.Cashfree({
        mode: isProduction ? "production" : "sandbox",
      });

      const checkoutOptions = {
        paymentSessionId: order.paymentSessionId,
        redirectTarget: "_modal",
      };

      cashfree
        .checkout(checkoutOptions)
        .then(async (result) => {
          if (result.error) {
            if (
              result.error.code === "MODAL_CLOSED" ||
              result.error.message?.includes("closed")
            ) {
              setStatus("CANCELLED");
              return;
            }
            setStatus("FAILED");
            setError(result.error.message || "Cashfree payment failed.");
            return;
          }

          setStatus("VERIFYING");
          try {
            const payId = result.paymentDetails?.paymentId || order.orderId;
            await verifyPayment({
              provider: "CASHFREE",
              orderId: order.orderId,
              paymentId: payId,
            });
            setPaymentDetails({
              orderId: order.orderId,
              paymentId: payId,
              amount: order.amountPaise,
            });
            setStatus("SUCCESS");
          } catch (err) {
            setStatus("FAILED");
            setError(err.message || "Verification with server failed.");
          }
        })
        .catch((err) => {
          setStatus("FAILED");
          setError(err.message || "Cashfree checkout error occurred.");
        });
    } catch (err) {
      // Cashfree needs a mobile number the account does not have: ask.
      if (payer.askedFor(err)) {
        setStatus("IDLE");
        return;
      }
      setStatus("FAILED");
      setError(err.message || "Order creation failed.");
    }
  }

  function handleContinue() {
    if (type === "VERIFICATION_FEE") {
      navigate("/verifications?success=1", { replace: true });
    } else if (type === "PROFESSIONAL_MEMBERSHIP") {
      navigate("/billing", { replace: true });
    } else {
      navigate("/earnings", { replace: true });
    }
  }

  // Loading Screens & States
  if (
    status === "CREATING_ORDER" ||
    status === "CHECKOUT_OPEN" ||
    status === "VERIFYING" ||
    status === "PROCESSING" ||
    status === "PENDING"
  ) {
    const textMap = {
      CREATING_ORDER: "Initializing Cashfree secure transaction...",
      CHECKOUT_OPEN: "Opening official Cashfree Payments modal...",
      VERIFYING: "Verifying payment with bank servers...",
      PROCESSING: "Processing transaction records...",
      PENDING: "Payment confirmation is pending...",
    };
    return (
      <AppShell>
        <div style={fullScreenLoaderStyle}>
          <LiquidLoader
            text={textMap[status] || "Loading payment checkout..."}
          />
          <div style={{ marginTop: 24, fontSize: 13, color: C.textLight }}>
            Please complete the payment in the official Cashfree modal overlay.
          </div>
        </div>
      </AppShell>
    );
  }

  // SUCCESS Screen
  if (status === "SUCCESS") {
    return (
      <AppShell maxWidth={600}>
        <div style={{ ...successCardStyle, padding: isMobile ? 20 : 40 }}>
          <div style={successIconContainerStyle}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: C.green }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              margin: "16px 0 8px",
              color: C.textDark,
            }}
          >
            Payment Successful!
          </h1>
          <p
            style={{
              fontSize: 14,
              color: C.textMed,
              margin: "0 0 24px",
              lineHeight: 1.5,
            }}
          >
            Thank you. Your payment was captured, verified, and your request is
            now active.
          </p>

          <div
            style={{
              ...detailsBoxStyle,
              padding: isMobile ? "12px 16px" : "16px 20px",
            }}
          >
            <div style={detailsRowStyle}>
              <span style={{ color: C.textLight }}>Order reference:</span>
              <span
                style={{
                  fontWeight: 600,
                  color: C.textDark,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {paymentDetails?.orderId}
              </span>
            </div>
            <div style={detailsRowStyle}>
              <span style={{ color: C.textLight }}>Payment ID:</span>
              <span
                style={{
                  fontWeight: 600,
                  color: C.textDark,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {paymentDetails?.paymentId}
              </span>
            </div>
            <div style={detailsRowStyle}>
              <span style={{ color: C.textLight }}>Amount Paid:</span>
              <span
                style={{
                  fontWeight: 700,
                  color: C.green,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatRupees(paymentDetails?.amount ?? 0)}
              </span>
            </div>
            <div style={detailsRowStyle}>
              <span style={{ color: C.textLight }}>Gateway provider:</span>
              <span style={{ fontWeight: 600, color: C.textDark }}>
                CASHFREE
              </span>
            </div>
          </div>

          <button
            onClick={handleContinue}
            style={{
              ...primaryButtonStyle,
              background: C.green,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Continue to Platform
          </button>
        </div>
        <Toast
          type="success"
          title="Tax Invoice Emailed!"
          message="Your payment was captured and your official tax invoice PDF was sent to your registered email address."
          duration={8000}
        />
      </AppShell>
    );
  }

  // FAILED, CANCELLED, or IDLE Screen
  const isCancelled = status === "CANCELLED";
  const hasError = status === "FAILED" || error;

  return (
    <AppShell maxWidth={640}>
      <div style={{ margin: isMobile ? "16px 0 12px" : "24px 0 16px" }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 800,
            margin: "0 0 4px",
            color: C.textDark,
            letterSpacing: "-0.4px",
          }}
        >
          Secure Payment Checkout
        </h1>
        <p style={{ fontSize: 13, color: C.textMed, margin: 0 }}>
          Secured by Cashfree Payments (UPI, Cards, Net Banking & Wallets).
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Failure Alert Box */}
        {hasError && (
          <div style={alertBoxStyle(C.redLight, C.red)}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Transaction Failed
            </div>
            <div style={{ fontSize: 13 }}>
              {error ||
                "An error occurred while processing the payment. Please try again."}
            </div>
          </div>
        )}

        {/* Cancellation Alert Box */}
        {isCancelled && (
          <div style={alertBoxStyle(C.amberLight, C.amber)}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Payment Cancelled
            </div>
            <div style={{ fontSize: 13 }}>
              You closed the official payment modal. You can click below to
              re-open checkout.
            </div>
          </div>
        )}

        <div style={{ ...summaryCardStyle, padding: isMobile ? 16 : 28 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "0 0 16px",
              borderBottom: `1px solid ${C.divider}`,
              paddingBottom: 12,
            }}
          >
            <h2
              style={{
                fontSize: 16,
                fontWeight: 800,
                margin: 0,
                color: C.textDark,
              }}
            >
              Order Details
            </h2>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: C.primary,
                background: `${C.primary}12`,
                padding: "4px 10px",
                borderRadius: 12,
              }}
            >
              Official Invoice
            </span>
          </div>

          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.textDark,
              marginBottom: 6,
            }}
          >
            {info.label}
          </div>
          <p
            style={{
              fontSize: 13,
              color: C.textMed,
              margin: "0 0 20px",
              lineHeight: 1.5,
            }}
          >
            {info.description}
          </p>

          {/* Coupon Code Section - Available for verification fees */}
          {type === "VERIFICATION_FEE" && (
            <div
              style={{
                background: "#ffffff",
                padding: isMobile ? "12px 14px" : "14px 18px",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: appliedCoupon ? 8 : 10,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.textDark,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: C.primary }}
                  >
                    <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  Have a Verification Coupon Code?
                </span>
                {appliedCoupon && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#166534",
                      background: "#dcfce7",
                      padding: "2px 8px",
                      borderRadius: 999,
                    }}
                  >
                    {appliedCoupon.discountPercent}% Discount
                  </span>
                )}
              </div>

              {appliedCoupon ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 8,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 800,
                        color: "#15803d",
                        fontSize: 14,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {appliedCoupon.code}
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#166534", marginTop: 2 }}
                    >
                      {appliedCoupon.organisation?.name && (
                        <span style={{ fontWeight: 600 }}>
                          {appliedCoupon.organisation.name} •{" "}
                        </span>
                      )}
                      {appliedCoupon.institution?.name && (
                        <span style={{ fontWeight: 600 }}>
                          {appliedCoupon.institution.name} •{" "}
                        </span>
                      )}
                      {appliedCoupon.message ||
                        `₹${(appliedCoupon.discountAmountPaise / 100).toLocaleString("en-IN")} discount applied.`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#dc2626",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      placeholder="Enter coupon code (e.g. RELI50 or IIT50)"
                      value={couponCodeInput}
                      onChange={(e) => {
                        setCouponCodeInput(e.target.value.toUpperCase());
                        if (couponError) setCouponError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #cbd5e1",
                        fontSize: 13,
                        fontFamily: "monospace",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        outline: "none",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCodeInput.trim()}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 8,
                        background: couponCodeInput.trim()
                          ? C.primary
                          : "#94a3b8",
                        color: "#ffffff",
                        border: "none",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: couponCodeInput.trim()
                          ? "pointer"
                          : "not-allowed",
                        transition: "background 0.2s",
                      }}
                    >
                      {couponLoading ? "Checking..." : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#dc2626",
                        marginTop: 6,
                        fontWeight: 500,
                      }}
                    >
                      {couponError}
                    </div>
                  )}
                  {couponSuccessMsg && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#16a34a",
                        marginTop: 6,
                        fontWeight: 600,
                      }}
                    >
                      {couponSuccessMsg}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              background: "#f8fafc",
              padding: isMobile ? "12px 16px" : "16px 20px",
              borderRadius: 12,
              border: "1px solid #e2e8f0",
              marginBottom: 20,
            }}
          >
            <div>
              <span
                style={{ fontSize: 14, fontWeight: 700, color: C.textDark }}
              >
                Total Amount
              </span>
              {appliedCoupon && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#16a34a",
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  Coupon {appliedCoupon.code} ({appliedCoupon.discountPercent}%
                  OFF)
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              {appliedCoupon && (
                <div
                  style={{
                    fontSize: 14,
                    textDecoration: "line-through",
                    color: "#94a3b8",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatRupees(originalAmountPaise)}
                </div>
              )}
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: appliedCoupon ? "#16a34a" : "#0f172a",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {effectiveAmountPaise === 0
                  ? "FREE"
                  : formatRupees(effectiveAmountPaise)}
              </span>
            </div>
          </div>

          <PayerPhoneField payer={payer} className="mb-6" />

          {/* Terms Acceptance Checkbox */}
          <div
            style={{
              marginBottom: 24,
              padding: "14px 16px",
              background: termsAccepted ? "#f0fdf4" : "#f8fafc",
              border: `1.5px solid ${termsAccepted ? "#16a34a" : "#cbd5e1"}`,
              borderRadius: 10,
              transition: "all 0.2s ease",
            }}
          >
            <label
              htmlFor="terms-checkbox"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                cursor: "pointer",
                fontSize: 13,
                color: C.textDark,
                lineHeight: 1.5,
                userSelect: "none",
              }}
            >
              <input
                id="terms-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked && error?.includes("accept")) {
                    setError(null);
                  }
                }}
                style={{
                  marginTop: 2,
                  width: 18,
                  height: 18,
                  accentColor: C.primary,
                  cursor: "pointer",
                }}
              />
              <span>
                I agree to the{" "}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: C.primary,
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: C.primary,
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <button
              onClick={handlePay}
              disabled={!termsAccepted || !price.pricePaise}
              style={{
                width: "100%",
                padding: isMobile ? "14px 16px" : "16px",
                minHeight: 48,
                fontSize: 15,
                fontWeight: 700,
                color: "#ffffff",
                background: termsAccepted ? "#00b27a" : "#cbd5e1",
                border: 0,
                borderRadius: 10,
                cursor: termsAccepted ? "pointer" : "not-allowed",
                fontFamily: font,
                opacity: termsAccepted ? 1 : 0.6,
                boxShadow: termsAccepted
                  ? "0 6px 16px rgba(0, 178, 122, 0.25)"
                  : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <span>
                {effectiveAmountPaise === 0
                  ? "Confirm & Submit (Free with Coupon)"
                  : `Pay ${formatRupees(effectiveAmountPaise)} Now`}
              </span>
            </button>

            <button
              onClick={() => navigate(-1)}
              style={{
                ...textLinkStyle,
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Cancel and Return
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const errorContainerStyle = {
  background: C.bg,
  border: `1.5px solid ${C.dividerStrong}`,
  borderRadius: 8,
  padding: 32,
  textAlign: "center",
  marginTop: 40,
};

const fullScreenLoaderStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "50vh",
  textAlign: "center",
};

const successCardStyle = {
  background: C.bg,
  border: `1.5px solid ${C.dividerStrong}`,
  borderRadius: 8,
  padding: 40,
  textAlign: "center",
  marginTop: 20,
};

const successIconContainerStyle = {
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: C.greenLight,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
};

const detailsBoxStyle = {
  background: C.bgMuted,
  borderRadius: 6,
  padding: "16px 20px",
  marginBottom: 28,
  textAlign: "left",
  border: `1px solid ${C.divider}`,
};

const detailsRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 12,
  padding: "6px 0",
  borderBottom: `1px solid ${C.divider}`,
  lineHeight: 1.5,
  alignItems: "center",
};

const alertBoxStyle = (bg, color) => ({
  background: bg,
  color: color,
  borderRadius: 8,
  padding: "16px 20px",
  lineHeight: 1.4,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  boxShadow: `0 2px 8px ${bg}`,
});

const summaryCardStyle = {
  background: C.bg,
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  padding: 28,
  boxShadow:
    "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.03)",
  position: "sticky",
  top: 24,
};

const primaryButtonStyle = {
  width: "100%",
  padding: "14px",
  fontSize: 14,
  fontWeight: 700,
  color: "white",
  border: 0,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
  transition: "opacity 0.15s ease",
};

const secondaryButtonStyle = {
  marginTop: 16,
  padding: "10px 20px",
  fontSize: 13,
  fontWeight: 600,
  color: C.textDark,
  background: "transparent",
  border: `1.5px solid ${C.dividerStrong}`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};

const textLinkStyle = {
  display: "block",
  width: "100%",
  textAlign: "center",
  background: "none",
  border: 0,
  fontSize: 12,
  color: C.textLight,
  textDecoration: "underline",
  cursor: "pointer",
  marginTop: 14,
  fontFamily: font,
};
